import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Calculator,
  ArrowRight,
  Download,
  Save,
  CheckCircle,
  Info,
  Home,
  Car,
  Smartphone,
  Utensils,
  Briefcase,
  PiggyBank,
  Building2,
  HelpCircle,
  Euro,
  Percent,
  Users,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ========== CALCUL FUNCTIONS ==========

// Calcul IS (Impôt sur les Sociétés) 2026
const calculateIS = (benefice) => {
  if (benefice <= 0) return 0;
  // Taux réduit 15% jusqu'à 42 500€, puis 25%
  if (benefice <= 42500) {
    return benefice * 0.15;
  }
  return 42500 * 0.15 + (benefice - 42500) * 0.25;
};

// Calcul IR barème progressif 2026 (officiel)
const calculateIR = (revenuImposable, nbParts = 1) => {
  // Barème IR 2026 officiel
  const tranches = [
    { limite: 11600, taux: 0 },
    { limite: 29579, taux: 0.11 },
    { limite: 84577, taux: 0.30 },
    { limite: 181917, taux: 0.41 },
    { limite: Infinity, taux: 0.45 },
  ];

  const quotientFamilial = revenuImposable / nbParts;
  let impotParPart = 0;
  let revenuRestant = quotientFamilial;
  let tranchePrecedente = 0;

  for (const tranche of tranches) {
    if (revenuRestant <= 0) break;
    
    const montantDansTranche = Math.min(revenuRestant, tranche.limite - tranchePrecedente);
    impotParPart += montantDansTranche * tranche.taux;
    revenuRestant -= montantDansTranche;
    tranchePrecedente = tranche.limite;
  }

  // Plafonnement du quotient familial (1 759€ par demi-part au-delà de 2)
  let impotTotal = impotParPart * nbParts;
  
  if (nbParts > 2) {
    const demiPartsSupp = (nbParts - 2) * 2; // Nombre de demi-parts supplémentaires
    const plafond = 1759 * demiPartsSupp;
    const impotSansQF = calculateIR(revenuImposable, 2); // Impôt avec 2 parts
    const economieQF = impotSansQF - impotTotal;
    
    if (economieQF > plafond) {
      impotTotal = impotSansQF - plafond;
    }
  }

  return Math.max(0, Math.round(impotTotal));
};

// Détail des tranches IR pour affichage
const getDetailIR = (revenuImposable, nbParts) => {
  const tranches = [
    { limite: 11600, taux: 0, nom: "0%" },
    { limite: 29579, taux: 0.11, nom: "11%" },
    { limite: 84577, taux: 0.30, nom: "30%" },
    { limite: 181917, taux: 0.41, nom: "41%" },
    { limite: Infinity, taux: 0.45, nom: "45%" },
  ];

  const quotient = revenuImposable / nbParts;
  const details = [];
  let revenuRestant = quotient;
  let tranchePrecedente = 0;

  for (const tranche of tranches) {
    if (revenuRestant <= 0) break;
    
    const montant = Math.min(revenuRestant, tranche.limite - tranchePrecedente);
    if (montant > 0) {
      details.push({
        tranche: tranche.nom,
        montant: Math.round(montant),
        impot: Math.round(montant * tranche.taux),
      });
    }
    revenuRestant -= montant;
    tranchePrecedente = tranche.limite;
  }

  return details;
};

// Calcul du nombre de parts fiscales
const getNbParts = (situation, nbEnfants) => {
  let parts = 1; // Célibataire de base
  
  if (situation === "couple") {
    parts = 2;
  } else if (situation === "parent_isole") {
    parts = 1.5; // Premier enfant compte pour 1 part
  }
  
  // Enfants
  if (nbEnfants >= 1) {
    if (situation === "parent_isole") {
      // Premier enfant déjà compté dans 1.5
      parts += (nbEnfants - 1) * 0.5;
      if (nbEnfants >= 3) parts += 0.5; // 3ème enfant = 1 part
    } else {
      parts += nbEnfants * 0.5;
      if (nbEnfants >= 3) parts += 0.5; // À partir du 3ème = 1 part
    }
  }
  
  return parts;
};

// SASU - Président assimilé salarié
const calculateSASU = (ca, chargesDeductibles, salaireNetSouhaite, nbParts, revenuConjoint = 0) => {
  // En SASU, le président est assimilé salarié
  // Charges patronales: ~42% du brut
  // Charges salariales: ~22% du brut  
  // Net = Brut - Charges salariales = Brut * 0.78
  
  const tauxPatronal = 0.42;
  const tauxSalarial = 0.22;
  
  // Pour obtenir le salaire net souhaité:
  // Net = Brut * (1 - tauxSalarial)
  // Brut = Net / (1 - tauxSalarial)
  const salaireBrut = salaireNetSouhaite / (1 - tauxSalarial);
  const chargesSalariales = salaireBrut * tauxSalarial;
  const chargesPatronales = salaireBrut * tauxPatronal;
  
  // Coût total pour la société
  const coutTotalSalaire = salaireBrut + chargesPatronales;
  
  // Vérification : le net doit être correct
  const salaireNetCalcule = salaireBrut - chargesSalariales;
  
  // Résultat avant IS
  const resultatAvantIS = ca - chargesDeductibles - coutTotalSalaire;
  
  // IS sur le bénéfice restant
  const is = calculateIS(Math.max(0, resultatAvantIS));
  
  // Cash restant dans la société
  const cashSociete = Math.max(0, resultatAvantIS - is);
  
  // IR sur le salaire net (après abattement 10%, plafonné à 14 171€)
  const abattement = Math.min(salaireNetSouhaite * 0.10, 14171);
  const revenuImposableFreelance = salaireNetSouhaite - abattement;
  
  // Revenu imposable total du foyer (freelance + conjoint)
  const revenuImposableFoyer = revenuImposableFreelance + revenuConjoint;
  const ir = calculateIR(revenuImposableFoyer, nbParts);
  
  // Part de l'IR attribuable au freelance (au prorata)
  const irFreelance = revenuConjoint > 0 
    ? Math.round(ir * (revenuImposableFreelance / revenuImposableFoyer))
    : ir;
  
  // Net disponible pour le freelance (après IR)
  const netDisponible = salaireNetSouhaite - irFreelance;
  
  // Patrimoine total = net perso + cash société
  const patrimoineTotal = netDisponible + cashSociete;
  
  // Taux de charges sociales total (patronal + salarial) par rapport au net
  const tauxChargesTotal = ((chargesPatronales + chargesSalariales) / salaireNetSouhaite * 100).toFixed(0);
  
  return {
    name: "SASU",
    shortName: "SASU",
    salaireBrut: Math.round(salaireBrut),
    chargesPatronales: Math.round(chargesPatronales),
    chargesSalariales: Math.round(chargesSalariales),
    totalChargesSociales: Math.round(chargesPatronales + chargesSalariales),
    coutTotalSalaire: Math.round(coutTotalSalaire),
    salaireNetAvantIR: Math.round(salaireNetCalcule),
    chargesDeductibles: Math.round(chargesDeductibles),
    resultatAvantIS: Math.round(Math.max(0, resultatAvantIS)),
    is: Math.round(is),
    cashSociete: Math.round(cashSociete),
    revenuImposable: Math.round(revenuImposableFreelance),
    ir: Math.round(irFreelance),
    netDisponible: Math.round(netDisponible),
    patrimoineTotal: Math.round(patrimoineTotal),
    tauxChargesSociales: tauxChargesTotal,
    description: "Assimilé salarié, régime général",
    avantages: [
      "Protection sociale maximale (régime général)",
      "Dividendes flat tax 30% (sans charges sociales)",
      "Cash dans la société réinvestissable",
      "Image professionnelle",
    ],
    inconvenients: [
      "Charges sociales élevées (~64% du net)",
      "Comptabilité obligatoire",
      "Coûts de gestion",
    ],
  };
};

// EURL à l'IS - Gérant TNS
const calculateEURL = (ca, chargesDeductibles, salaireNetSouhaite, nbParts, revenuConjoint = 0) => {
  // En EURL IS, le gérant est TNS
  // Charges TNS: ~45% du net (sur la rémunération nette)
  // Rémunération versée = Net souhaité
  // Charges = Net * 0.45
  
  const tauxTNS = 0.45;
  
  const remunerationNette = salaireNetSouhaite;
  const chargesTNS = remunerationNette * tauxTNS;
  const coutTotalRemuneration = remunerationNette + chargesTNS;
  
  // Résultat avant IS
  const resultatAvantIS = ca - chargesDeductibles - coutTotalRemuneration;
  
  // IS sur le bénéfice restant
  const is = calculateIS(Math.max(0, resultatAvantIS));
  
  // Cash restant dans la société
  const cashSociete = Math.max(0, resultatAvantIS - is);
  
  // IR sur la rémunération (après abattement 10%)
  const abattement = Math.min(remunerationNette * 0.10, 14171);
  const revenuImposableFreelance = remunerationNette - abattement;
  
  // Revenu imposable total du foyer
  const revenuImposableFoyer = revenuImposableFreelance + revenuConjoint;
  const ir = calculateIR(revenuImposableFoyer, nbParts);
  
  // Part de l'IR attribuable au freelance
  const irFreelance = revenuConjoint > 0 
    ? Math.round(ir * (revenuImposableFreelance / revenuImposableFoyer))
    : ir;
  
  // Net disponible pour le freelance
  const netDisponible = remunerationNette - irFreelance;
  
  // Patrimoine total
  const patrimoineTotal = netDisponible + cashSociete;
  
  return {
    name: "EURL (IS)",
    shortName: "EURL",
    salaireBrut: Math.round(remunerationNette), // En TNS, on parle de rémunération nette
    chargesPatronales: 0,
    chargesSalariales: Math.round(chargesTNS),
    totalChargesSociales: Math.round(chargesTNS),
    coutTotalSalaire: Math.round(coutTotalRemuneration),
    salaireNetAvantIR: Math.round(remunerationNette),
    chargesDeductibles: Math.round(chargesDeductibles),
    resultatAvantIS: Math.round(Math.max(0, resultatAvantIS)),
    is: Math.round(is),
    cashSociete: Math.round(cashSociete),
    revenuImposable: Math.round(revenuImposableFreelance),
    ir: Math.round(irFreelance),
    netDisponible: Math.round(netDisponible),
    patrimoineTotal: Math.round(patrimoineTotal),
    tauxChargesSociales: "45",
    description: "TNS, charges sociales réduites",
    avantages: [
      "Charges sociales plus faibles (~45%)",
      "Cash réinvestissable en société",
      "Flexibilité de rémunération",
      "Déduction des charges réelles",
    ],
    inconvenients: [
      "Protection sociale TNS (moins complète)",
      "Dividendes > 10% capital soumis à charges",
      "Comptabilité obligatoire",
    ],
  };
};

// Micro-entreprise
const calculateMicro = (ca, chargesReelles, salaireNetSouhaite, nbParts, revenuConjoint = 0) => {
  // En micro, pas de déduction de charges, forfait de 34% (BNC)
  // Charges sociales: 25.6% du CA (taux 2025-2026 pour libéraux)
  const tauxCharges = 0.256;
  const chargesSociales = ca * tauxCharges;
  
  // Revenu après charges sociales
  const revenuApresCharges = ca - chargesSociales;
  
  // Les charges réelles ne sont PAS déductibles en micro
  // Mais le freelance doit quand même les payer
  const revenuApresChargesReelles = revenuApresCharges - chargesReelles;
  
  // IR avec abattement forfaitaire de 34% (BNC)
  const revenuImposableFreelance = ca * 0.66; // 66% du CA imposable
  
  // Revenu imposable total du foyer
  const revenuImposableFoyer = revenuImposableFreelance + revenuConjoint;
  const ir = calculateIR(revenuImposableFoyer, nbParts);
  
  // Part de l'IR attribuable au freelance
  const irFreelance = revenuConjoint > 0 
    ? Math.round(ir * (revenuImposableFreelance / revenuImposableFoyer))
    : ir;
  
  // Net disponible
  const netDisponible = revenuApresChargesReelles - irFreelance;
  
  // Pas de cash société en micro
  const cashSociete = 0;
  const patrimoineTotal = netDisponible;
  
  return {
    name: "Micro-entreprise",
    shortName: "Micro",
    salaireBrut: Math.round(ca),
    chargesPatronales: 0,
    chargesSalariales: Math.round(chargesSociales),
    totalChargesSociales: Math.round(chargesSociales),
    coutTotalSalaire: Math.round(chargesSociales),
    salaireNetAvantIR: Math.round(revenuApresCharges),
    chargesDeductibles: 0,
    chargesPayees: Math.round(chargesReelles),
    resultatAvantIS: 0,
    is: 0,
    cashSociete: 0,
    revenuImposable: Math.round(revenuImposableFreelance),
    ir: Math.round(irFreelance),
    netDisponible: Math.round(netDisponible),
    patrimoineTotal: Math.round(patrimoineTotal),
    tauxChargesSociales: "25.6",
    description: "Simplicité maximale, pas de déduction",
    avantages: [
      "Simplicité administrative maximale",
      "Pas de comptabilité",
      "Charges calculées sur CA réel",
      "Pas de minimum de charges",
    ],
    inconvenients: [
      "Pas de déduction de charges réelles",
      "Plafond CA (77 700€ BNC)",
      "Pas d'optimisation possible",
      "Pas de cash réinvestissable",
    ],
    warning: ca > 77700 ? "⚠️ Dépassement du plafond micro-BNC (77 700€)" : null,
  };
};

// Portage salarial
const calculatePortage = (ca, chargesDeductibles, salaireNetSouhaite, nbParts, revenuConjoint = 0) => {
  // Commission de portage: 5% du CA HT
  const tauxCommission = 0.05;
  const commissionPortage = ca * tauxCommission;
  const caApresCommission = ca - commissionPortage;
  
  // Le reste est converti en salaire
  // En portage, charges patronales légèrement plus élevées car inclut des frais de gestion
  // Charges patronales: ~47%, Charges salariales: ~23%
  // Net = Brut * 0.77
  const tauxPatronal = 0.47;
  const tauxSalarial = 0.23;
  
  // Budget disponible pour le salaire après commission et charges déductibles
  const budgetSalaire = caApresCommission - chargesDeductibles;
  
  // Calcul du brut maximum possible avec ce budget
  // Budget = Brut + Brut * tauxPatronal = Brut * (1 + tauxPatronal)
  const brutMaxPossible = budgetSalaire / (1 + tauxPatronal);
  const netMaxPossible = brutMaxPossible * (1 - tauxSalarial);
  
  // Utiliser le minimum entre ce qu'on veut et ce qu'on peut
  let salaireBrut, salaireNet, chargesPatronales, chargesSalariales;
  
  if (netMaxPossible >= salaireNetSouhaite) {
    // On peut se verser le salaire souhaité
    salaireNet = salaireNetSouhaite;
    salaireBrut = salaireNet / (1 - tauxSalarial);
  } else {
    // On prend le maximum possible
    salaireNet = netMaxPossible;
    salaireBrut = brutMaxPossible;
  }
  
  chargesPatronales = salaireBrut * tauxPatronal;
  chargesSalariales = salaireBrut * tauxSalarial;
  const coutTotalSalaire = salaireBrut + chargesPatronales;
  
  // IR sur le salaire net (après abattement 10%)
  const abattement = Math.min(salaireNet * 0.10, 14171);
  const revenuImposableFreelance = salaireNet - abattement;
  
  // Revenu imposable total du foyer
  const revenuImposableFoyer = revenuImposableFreelance + revenuConjoint;
  const ir = calculateIR(revenuImposableFoyer, nbParts);
  
  // Part de l'IR attribuable au freelance
  const irFreelance = revenuConjoint > 0 
    ? Math.round(ir * (revenuImposableFreelance / revenuImposableFoyer))
    : ir;
  
  // Net disponible
  const netDisponible = salaireNet - irFreelance;
  
  // Pas de cash société en portage
  const patrimoineTotal = netDisponible;
  
  // Taux de charges total
  const tauxTotal = ((chargesPatronales + chargesSalariales) / salaireNet * 100).toFixed(0);
  
  return {
    name: "Portage salarial",
    shortName: "Portage",
    salaireBrut: Math.round(salaireBrut),
    chargesPatronales: Math.round(chargesPatronales),
    chargesSalariales: Math.round(chargesSalariales),
    totalChargesSociales: Math.round(chargesPatronales + chargesSalariales),
    commissionPortage: Math.round(commissionPortage),
    coutTotalSalaire: Math.round(coutTotalSalaire + commissionPortage),
    salaireNetAvantIR: Math.round(salaireNet),
    chargesDeductibles: Math.round(chargesDeductibles),
    resultatAvantIS: 0,
    is: 0,
    cashSociete: 0,
    revenuImposable: Math.round(revenuImposableFreelance),
    ir: Math.round(irFreelance),
    netDisponible: Math.round(netDisponible),
    patrimoineTotal: Math.round(patrimoineTotal),
    tauxChargesSociales: tauxTotal,
    description: "Salarié, protection maximale + chômage",
    avantages: [
      "Protection sociale maximale + chômage",
      "Pas de création de société",
      "Simplicité totale",
      "Pas de gestion administrative",
    ],
    inconvenients: [
      "Commission de portage (5%)",
      "Pas de cash réinvestissable",
      "Charges sociales élevées",
      "Revenus limités par le CA",
    ],
  };
};

// ========== COMPONENT ==========

export default function SimulateurPage() {
  const [formData, setFormData] = useState({
    chiffreAffaires: 80000,
    salaireNetSouhaite: 3500,
    // Charges déductibles lifestyle
    loyer: 800,
    pourcentageLoyer: 35,
    voiture: 300,
    pourcentageVoiture: 50,
    telephone: 50,
    pourcentageTelephone: 80,
    internet: 40,
    pourcentageInternet: 50,
    repas: 200,
    pourcentageRepas: 50,
    autresCharges: 200,
    // Situation fiscale
    situationFamiliale: "celibataire",
    nbEnfants: 0,
    revenuConjoint: 0, // Revenu net imposable annuel du conjoint
  });

  const [results, setResults] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [leadForm, setLeadForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("inputs");

  const calculateChargesDeductibles = () => {
    const loyerDeductible = formData.loyer * (formData.pourcentageLoyer / 100) * 12;
    const voitureDeductible = formData.voiture * (formData.pourcentageVoiture / 100) * 12;
    const telephoneDeductible = formData.telephone * (formData.pourcentageTelephone / 100) * 12;
    const internetDeductible = formData.internet * (formData.pourcentageInternet / 100) * 12;
    const repasDeductible = formData.repas * (formData.pourcentageRepas / 100) * 12;
    const autresDeductible = formData.autresCharges * 12;
    
    return loyerDeductible + voitureDeductible + telephoneDeductible + internetDeductible + repasDeductible + autresDeductible;
  };

  const calculateChargesReelles = () => {
    return calculateChargesDeductibles(); // Même montant, juste pas déductible en micro
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const calculateResults = () => {
    const ca = formData.chiffreAffaires;
    const salaireAnnuelSouhaite = formData.salaireNetSouhaite * 12;
    const chargesDeductibles = calculateChargesDeductibles();
    const chargesReelles = calculateChargesReelles();
    const nbParts = getNbParts(formData.situationFamiliale, formData.nbEnfants);
    const revenuConjoint = formData.situationFamiliale === "couple" ? formData.revenuConjoint : 0;

    const sasu = calculateSASU(ca, chargesDeductibles, salaireAnnuelSouhaite, nbParts, revenuConjoint);
    const eurl = calculateEURL(ca, chargesDeductibles, salaireAnnuelSouhaite, nbParts, revenuConjoint);
    const micro = calculateMicro(ca, chargesReelles, salaireAnnuelSouhaite, nbParts, revenuConjoint);
    const portage = calculatePortage(ca, chargesDeductibles, salaireAnnuelSouhaite, nbParts, revenuConjoint);

    const allResults = [sasu, eurl, micro, portage].sort((a, b) => b.patrimoineTotal - a.patrimoineTotal);

    setResults({
      statuts: allResults,
      recommended: allResults[0],
      inputs: {
        ca,
        salaireNetSouhaite: salaireAnnuelSouhaite,
        chargesDeductibles,
        chargesReelles,
        nbParts,
        revenuConjoint,
      },
    });
    
    setActiveTab("results");
  };

  const saveSimulation = () => {
    if (!results) return;
    
    const simulations = JSON.parse(localStorage.getItem("freelanceurs_simulations") || "[]");
    const simulation = {
      id: Date.now(),
      date: new Date().toISOString(),
      inputs: results.inputs,
      recommended: results.recommended.name,
      patrimoineTotal: results.recommended.patrimoineTotal,
    };
    
    const updated = [simulation, ...simulations].slice(0, 10);
    localStorage.setItem("freelanceurs_simulations", JSON.stringify(updated));
    toast.success("Simulation sauvegardée !");
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/simulation-leads`, {
        first_name: leadForm.firstName,
        last_name: leadForm.lastName,
        email: leadForm.email,
        phone: leadForm.phone,
        simulation_data: results,
      });

      generatePDF();
      toast.success("Votre simulation a été envoyée !");
      setShowModal(false);
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const generatePDF = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Simulation FREELANCEURS.io</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #1e293b; }
          h1 { color: #0f172a; border-bottom: 3px solid #52fbb0; padding-bottom: 10px; }
          h2 { color: #334155; margin-top: 30px; }
          .stat { background: #f8fafc; padding: 15px; margin: 10px 0; border-radius: 8px; border-left: 4px solid #52fbb0; }
          .stat-label { color: #64748b; font-size: 14px; }
          .stat-value { color: #0f172a; font-size: 24px; font-weight: bold; }
          .recommended { background: linear-gradient(135deg, #52fbb0 0%, #34d399 100%); color: #0f172a; padding: 20px; border-radius: 12px; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #f8fafc; font-weight: 600; }
          .highlight { background: #f0fdf4; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
          .green { color: #059669; }
        </style>
      </head>
      <body>
        <h1>FREELANCEURS.io - Comparatif des statuts</h1>
        <p>Simulation du ${new Date().toLocaleDateString('fr-FR')} • ${results.inputs.nbParts} part(s) fiscale(s)</p>
        
        <h2>Paramètres</h2>
        <div class="stat">
          <div class="stat-label">CA annuel</div>
          <div class="stat-value">${results.inputs.ca.toLocaleString('fr-FR')} €</div>
        </div>
        <div class="stat">
          <div class="stat-label">Besoin net mensuel</div>
          <div class="stat-value">${(results.inputs.salaireNetSouhaite / 12).toLocaleString('fr-FR')} €</div>
        </div>
        <div class="stat">
          <div class="stat-label">Charges déductibles annuelles</div>
          <div class="stat-value">${results.inputs.chargesDeductibles.toLocaleString('fr-FR')} €</div>
        </div>
        
        <div class="recommended">
          <h2 style="margin-top: 0;">✓ Recommandé : ${results.recommended.name}</h2>
          <p style="font-size: 18px; margin: 0;">
            Patrimoine total : <strong>${results.recommended.patrimoineTotal.toLocaleString('fr-FR')} €/an</strong>
          </p>
        </div>
        
        <h2>Comparaison détaillée</h2>
        <table>
          <thead>
            <tr>
              <th>Indicateur</th>
              ${results.statuts.map(s => `<th>${s.shortName}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Salaire brut</td>
              ${results.statuts.map(s => `<td>${s.salaireBrut.toLocaleString('fr-FR')} €</td>`).join('')}
            </tr>
            <tr>
              <td>Charges sociales</td>
              ${results.statuts.map(s => `<td>${s.totalChargesSociales.toLocaleString('fr-FR')} € (${s.tauxChargesSociales}%)</td>`).join('')}
            </tr>
            <tr>
              <td>Net avant IR</td>
              ${results.statuts.map(s => `<td>${s.salaireNetAvantIR.toLocaleString('fr-FR')} €</td>`).join('')}
            </tr>
            <tr>
              <td>Impôt sur le revenu</td>
              ${results.statuts.map(s => `<td>${s.ir.toLocaleString('fr-FR')} €</td>`).join('')}
            </tr>
            <tr>
              <td><strong>Net disponible</strong></td>
              ${results.statuts.map(s => `<td><strong>${s.netDisponible.toLocaleString('fr-FR')} €</strong></td>`).join('')}
            </tr>
            <tr>
              <td>IS</td>
              ${results.statuts.map(s => `<td>${s.is > 0 ? s.is.toLocaleString('fr-FR') + ' €' : '-'}</td>`).join('')}
            </tr>
            <tr class="highlight">
              <td><strong>Cash société</strong></td>
              ${results.statuts.map(s => `<td class="green"><strong>${s.cashSociete > 0 ? s.cashSociete.toLocaleString('fr-FR') + ' €' : '-'}</strong></td>`).join('')}
            </tr>
            <tr style="background: #1e293b; color: white;">
              <td><strong>PATRIMOINE TOTAL</strong></td>
              ${results.statuts.map((s, i) => `<td style="${i === 0 ? 'color: #52fbb0;' : ''}"><strong>${s.patrimoineTotal.toLocaleString('fr-FR')} €</strong></td>`).join('')}
            </tr>
          </tbody>
        </table>
        
        <div class="footer">
          <p><strong>FREELANCEURS.io</strong> - Le prix de la sérénité</p>
          <p>Estimations indicatives basées sur le barème IR 2026. Ne constitue pas un conseil fiscal.</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const chargesDeductibles = calculateChargesDeductibles();
  const nbParts = getNbParts(formData.situationFamiliale, formData.nbEnfants);

  const chartDataPatrimoine = results?.statuts.map((s) => ({
    name: s.shortName,
    "Net disponible": s.netDisponible,
    "Cash société": s.cashSociete,
  }));

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-50 py-12 md:py-20" data-testid="simulateur-page">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Calculator className="h-4 w-4" />
              Simulateur avancé
            </div>
            <h1 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-4">
              Comparez votre patrimoine selon les statuts
            </h1>
            <p className="text-slate-600 text-lg max-w-3xl mx-auto">
              Calculs basés sur le <strong>barème IR 2026</strong>. Optimisez votre rémunération 
              et le cash réinvestissable dans votre société.
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="inputs" className="text-base py-3">
                1. Paramètres
              </TabsTrigger>
              <TabsTrigger value="results" className="text-base py-3" disabled={!results}>
                2. Résultats
              </TabsTrigger>
            </TabsList>

            {/* INPUTS TAB */}
            <TabsContent value="inputs">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Colonne 1: CA, besoin et situation fiscale */}
                <Card className="lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Euro className="h-5 w-5 text-[#52fbb0]" />
                      Revenus & Situation fiscale
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* CA */}
                    <div>
                      <Label className="flex items-center gap-2">
                        Chiffre d'affaires annuel HT
                      </Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="number"
                          value={formData.chiffreAffaires}
                          onChange={(e) => handleInputChange("chiffreAffaires", parseInt(e.target.value) || 0)}
                          className="text-lg font-semibold"
                          data-testid="input-ca"
                        />
                        <span className="text-slate-500">€</span>
                      </div>
                      <Slider
                        value={[formData.chiffreAffaires]}
                        onValueChange={([v]) => handleInputChange("chiffreAffaires", v)}
                        min={30000}
                        max={250000}
                        step={5000}
                        className="mt-3"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>30k€</span>
                        <span>250k€</span>
                      </div>
                    </div>

                    {/* Salaire souhaité */}
                    <div>
                      <Label className="flex items-center gap-2">
                        Besoin net mensuel souhaité
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-4 w-4 text-slate-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Montant net que vous souhaitez percevoir chaque mois pour financer votre train de vie</p>
                          </TooltipContent>
                        </Tooltip>
                      </Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="number"
                          value={formData.salaireNetSouhaite}
                          onChange={(e) => handleInputChange("salaireNetSouhaite", parseInt(e.target.value) || 0)}
                          className="text-lg font-semibold"
                          data-testid="input-salaire"
                        />
                        <span className="text-slate-500">€/mois</span>
                      </div>
                      <Slider
                        value={[formData.salaireNetSouhaite]}
                        onValueChange={([v]) => handleInputChange("salaireNetSouhaite", v)}
                        min={1500}
                        max={12000}
                        step={100}
                        className="mt-3"
                      />
                    </div>

                    {/* Situation familiale */}
                    <div className="pt-4 border-t border-slate-200">
                      <Label className="flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4 text-slate-500" />
                        Situation familiale (pour l'IR)
                      </Label>
                      <Select
                        value={formData.situationFamiliale}
                        onValueChange={(v) => handleInputChange("situationFamiliale", v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="celibataire">Célibataire</SelectItem>
                          <SelectItem value="couple">Marié(e) / Pacsé(e)</SelectItem>
                          <SelectItem value="parent_isole">Parent isolé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Nombre d'enfants */}
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        Nombre d'enfants à charge
                      </Label>
                      <Select
                        value={formData.nbEnfants.toString()}
                        onValueChange={(v) => handleInputChange("nbEnfants", parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0 enfant</SelectItem>
                          <SelectItem value="1">1 enfant</SelectItem>
                          <SelectItem value="2">2 enfants</SelectItem>
                          <SelectItem value="3">3 enfants</SelectItem>
                          <SelectItem value="4">4 enfants ou +</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Revenu du conjoint (si en couple) */}
                    {formData.situationFamiliale === "couple" && (
                      <div>
                        <Label className="flex items-center gap-2">
                          Revenu net imposable du conjoint
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="h-4 w-4 text-slate-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">Revenu annuel net imposable de votre conjoint(e). Utilisé pour le calcul de l'IR en déclaration commune.</p>
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            type="number"
                            value={formData.revenuConjoint}
                            onChange={(e) => handleInputChange("revenuConjoint", parseInt(e.target.value) || 0)}
                            placeholder="0"
                            data-testid="input-revenu-conjoint"
                          />
                          <span className="text-slate-500">€/an</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Laissez 0 si votre conjoint(e) n'a pas de revenus
                        </p>
                      </div>
                    )}

                    {/* Récapitulatif parts fiscales */}
                    <div className="p-4 bg-slate-100 rounded-xl">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Parts fiscales :</span>
                        <span className="font-bold text-slate-900">{nbParts}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Utilisées pour le calcul de l'IR (barème progressif 2026)
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Colonne 2: Charges déductibles */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Briefcase className="h-5 w-5 text-[#52fbb0]" />
                      Charges professionnelles déductibles
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4 text-slate-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">Dépenses personnelles dont une partie peut être passée en charges professionnelles (domiciliation bureau, véhicule pro, etc.). Non déductibles en micro-entreprise.</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Loyer */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Home className="h-5 w-5 text-slate-600" />
                          <Label className="font-medium">Loyer (domiciliation bureau)</Label>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={formData.loyer}
                            onChange={(e) => handleInputChange("loyer", parseInt(e.target.value) || 0)}
                            className="flex-1"
                          />
                          <span className="flex items-center text-slate-500 text-sm">€/mois</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500 whitespace-nowrap">% pro:</span>
                          <Slider
                            value={[formData.pourcentageLoyer]}
                            onValueChange={([v]) => handleInputChange("pourcentageLoyer", v)}
                            min={0}
                            max={50}
                            step={5}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium w-12">{formData.pourcentageLoyer}%</span>
                        </div>
                        <p className="text-xs text-[#059669] font-medium">
                          = {Math.round(formData.loyer * formData.pourcentageLoyer / 100)} €/mois déductible
                        </p>
                      </div>

                      {/* Voiture */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Car className="h-5 w-5 text-slate-600" />
                          <Label className="font-medium">Véhicule (IK ou leasing)</Label>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={formData.voiture}
                            onChange={(e) => handleInputChange("voiture", parseInt(e.target.value) || 0)}
                            className="flex-1"
                          />
                          <span className="flex items-center text-slate-500 text-sm">€/mois</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500 whitespace-nowrap">% pro:</span>
                          <Slider
                            value={[formData.pourcentageVoiture]}
                            onValueChange={([v]) => handleInputChange("pourcentageVoiture", v)}
                            min={0}
                            max={100}
                            step={10}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium w-12">{formData.pourcentageVoiture}%</span>
                        </div>
                        <p className="text-xs text-[#059669] font-medium">
                          = {Math.round(formData.voiture * formData.pourcentageVoiture / 100)} €/mois déductible
                        </p>
                      </div>

                      {/* Téléphone */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Smartphone className="h-5 w-5 text-slate-600" />
                          <Label className="font-medium">Téléphone</Label>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={formData.telephone}
                            onChange={(e) => handleInputChange("telephone", parseInt(e.target.value) || 0)}
                            className="flex-1"
                          />
                          <span className="flex items-center text-slate-500 text-sm">€/mois</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500 whitespace-nowrap">% pro:</span>
                          <Slider
                            value={[formData.pourcentageTelephone]}
                            onValueChange={([v]) => handleInputChange("pourcentageTelephone", v)}
                            min={0}
                            max={100}
                            step={10}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium w-12">{formData.pourcentageTelephone}%</span>
                        </div>
                      </div>

                      {/* Internet */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-slate-600" />
                          <Label className="font-medium">Internet</Label>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={formData.internet}
                            onChange={(e) => handleInputChange("internet", parseInt(e.target.value) || 0)}
                            className="flex-1"
                          />
                          <span className="flex items-center text-slate-500 text-sm">€/mois</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500 whitespace-nowrap">% pro:</span>
                          <Slider
                            value={[formData.pourcentageInternet]}
                            onValueChange={([v]) => handleInputChange("pourcentageInternet", v)}
                            min={0}
                            max={100}
                            step={10}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium w-12">{formData.pourcentageInternet}%</span>
                        </div>
                      </div>

                      {/* Repas */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Utensils className="h-5 w-5 text-slate-600" />
                          <Label className="font-medium">Repas professionnels</Label>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={formData.repas}
                            onChange={(e) => handleInputChange("repas", parseInt(e.target.value) || 0)}
                            className="flex-1"
                          />
                          <span className="flex items-center text-slate-500 text-sm">€/mois</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500 whitespace-nowrap">% pro:</span>
                          <Slider
                            value={[formData.pourcentageRepas]}
                            onValueChange={([v]) => handleInputChange("pourcentageRepas", v)}
                            min={0}
                            max={100}
                            step={10}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium w-12">{formData.pourcentageRepas}%</span>
                        </div>
                      </div>

                      {/* Autres */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-5 w-5 text-slate-600" />
                          <Label className="font-medium">Autres (logiciels, formations...)</Label>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={formData.autresCharges}
                            onChange={(e) => handleInputChange("autresCharges", parseInt(e.target.value) || 0)}
                            className="flex-1"
                          />
                          <span className="flex items-center text-slate-500 text-sm">€/mois</span>
                        </div>
                        <p className="text-xs text-[#059669] font-medium">100% déductible</p>
                      </div>
                    </div>

                    {/* Récapitulatif */}
                    <div className="mt-6 p-4 bg-[#52fbb0]/10 rounded-xl border border-[#52fbb0]/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">Total charges déductibles</p>
                          <p className="text-sm text-slate-600">Réduisent le bénéfice imposable (sauf en micro)</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#059669]">{Math.round(chargesDeductibles).toLocaleString('fr-FR')} €</p>
                          <p className="text-sm text-slate-500">/an</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bouton calculer */}
              <div className="mt-8 text-center">
                <Button
                  onClick={calculateResults}
                  className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-bold rounded-full px-12 py-6 text-lg shadow-lg shadow-green-200/50"
                  data-testid="btn-calculate"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Calculer et comparer
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </TabsContent>

            {/* RESULTS TAB */}
            <TabsContent value="results">
              {results && (
                <div className="space-y-8">
                  {/* Statut recommandé */}
                  <Card className="bg-gradient-to-br from-[#52fbb0]/10 to-[#34d399]/10 border-[#52fbb0]/30 border-2">
                    <CardContent className="p-8">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                          <div className="flex items-center gap-2 text-[#059669] font-medium mb-2">
                            <CheckCircle className="h-5 w-5" />
                            Statut recommandé pour votre situation
                          </div>
                          <h2 className="font-manrope font-bold text-3xl text-slate-900 mb-2">
                            {results.recommended.name}
                          </h2>
                          <p className="text-slate-600">{results.recommended.description}</p>
                        </div>
                        <div className="text-right lg:text-right">
                          <p className="text-sm text-slate-500 mb-1">Patrimoine total annuel</p>
                          <p className="text-4xl font-bold text-[#059669]">
                            {results.recommended.patrimoineTotal.toLocaleString("fr-FR")} €
                          </p>
                          <div className="flex gap-4 mt-2 justify-end flex-wrap">
                            <div className="text-sm">
                              <span className="text-slate-500">Net perso:</span>{" "}
                              <span className="font-semibold">{results.recommended.netDisponible.toLocaleString("fr-FR")} €</span>
                            </div>
                            {results.recommended.cashSociete > 0 && (
                              <div className="text-sm">
                                <span className="text-slate-500">Cash société:</span>{" "}
                                <span className="font-semibold text-[#059669]">{results.recommended.cashSociete.toLocaleString("fr-FR")} €</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Graphique Patrimoine */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PiggyBank className="h-5 w-5 text-[#52fbb0]" />
                        Patrimoine total par statut
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartDataPatrimoine}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" />
                          <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                          <RechartsTooltip
                            formatter={(value) => [`${value.toLocaleString("fr-FR")} €`, ""]}
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                            }}
                          />
                          <Legend />
                          <Bar dataKey="Net disponible" stackId="a" fill="#52fbb0" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="Cash société" stackId="a" fill="#059669" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      <p className="text-xs text-slate-500 text-center mt-2">
                        Le cash société peut être réinvesti (immo, bourse) sans fiscalité supplémentaire
                      </p>
                    </CardContent>
                  </Card>

                  {/* Tableau comparatif détaillé */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Percent className="h-5 w-5 text-[#52fbb0]" />
                        Comparaison détaillée ({results.inputs.nbParts} part{results.inputs.nbParts > 1 ? 's' : ''} fiscale{results.inputs.nbParts > 1 ? 's' : ''})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-900">Indicateur</th>
                            {results.statuts.map((s, i) => (
                              <th key={s.name} className={`text-right py-3 px-4 font-semibold ${i === 0 ? 'text-[#059669] bg-[#52fbb0]/5' : 'text-slate-900'}`}>
                                {s.shortName}
                                {i === 0 && <span className="ml-1 text-xs">✓</span>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          <tr className="bg-slate-50">
                            <td colSpan={5} className="py-2 px-4 font-semibold text-slate-700 text-xs uppercase tracking-wide">
                              Coût de la rémunération
                            </td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4 text-slate-600">Salaire/Rémunération brut(e)</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                {s.salaireBrut.toLocaleString("fr-FR")} €
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-3 px-4 text-slate-600">
                              Charges sociales
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="h-3 w-3 text-slate-400 ml-1 inline" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">SASU: patronales+salariales sur brut<br/>EURL: TNS sur net<br/>Portage: patronales+salariales+commission</p>
                                </TooltipContent>
                              </Tooltip>
                            </td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                {s.totalChargesSociales.toLocaleString("fr-FR")} €
                                <span className="text-slate-400 text-xs ml-1">({s.tauxChargesSociales}%)</span>
                              </td>
                            ))}
                          </tr>
                          {results.statuts.some(s => s.commissionPortage) && (
                            <tr>
                              <td className="py-3 px-4 text-slate-600">Commission portage (5%)</td>
                              {results.statuts.map((s, i) => (
                                <td key={s.name} className={`text-right py-3 px-4 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                  {s.commissionPortage ? `${s.commissionPortage.toLocaleString("fr-FR")} €` : '-'}
                                </td>
                              ))}
                            </tr>
                          )}
                          <tr>
                            <td className="py-3 px-4 text-slate-600">Charges déductibles</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                {s.chargesDeductibles > 0 ? `${s.chargesDeductibles.toLocaleString("fr-FR")} €` : 
                                  <span className="text-amber-600">Non déductible</span>}
                              </td>
                            ))}
                          </tr>
                          <tr className="bg-slate-50">
                            <td className="py-3 px-4 font-semibold text-slate-900">Net avant impôt (IR)</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 font-semibold ${i === 0 ? 'bg-[#52fbb0]/10' : ''}`}>
                                {s.salaireNetAvantIR.toLocaleString("fr-FR")} €
                              </td>
                            ))}
                          </tr>
                          
                          <tr className="bg-slate-50">
                            <td colSpan={5} className="py-2 px-4 font-semibold text-slate-700 text-xs uppercase tracking-wide">
                              Impôts (Barème progressif IR 2026)
                            </td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4 text-slate-600">Revenu imposable (après abattement 10%)</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                {s.revenuImposable.toLocaleString("fr-FR")} €
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-3 px-4 text-slate-600 flex items-center gap-1">
                              Impôt sur le revenu (IR)
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="h-3 w-3 text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">
                                    Barème 2026 : 0% jusqu'à 11 497€, 11% de 11 497€ à 29 315€, 
                                    30% de 29 315€ à 83 823€, 41% au-delà
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 text-red-600 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                -{s.ir.toLocaleString("fr-FR")} €
                              </td>
                            ))}
                          </tr>
                          <tr className="bg-blue-50">
                            <td className="py-3 px-4 font-semibold text-slate-900">NET DISPONIBLE (perso)</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 font-bold text-lg ${i === 0 ? 'bg-[#52fbb0]/10 text-[#059669]' : ''}`}>
                                {s.netDisponible.toLocaleString("fr-FR")} €
                              </td>
                            ))}
                          </tr>

                          <tr className="bg-slate-50">
                            <td colSpan={5} className="py-2 px-4 font-semibold text-slate-700 text-xs uppercase tracking-wide">
                              Cash société (réinvestissable)
                            </td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4 text-slate-600">Résultat avant IS</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                {s.resultatAvantIS > 0 ? `${s.resultatAvantIS.toLocaleString("fr-FR")} €` : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-3 px-4 text-slate-600">Impôt sur les sociétés (IS 15%/25%)</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 ${i === 0 ? 'bg-[#52fbb0]/5' : ''}`}>
                                {s.is > 0 ? `-${s.is.toLocaleString("fr-FR")} €` : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr className="bg-green-50">
                            <td className="py-3 px-4 font-semibold text-[#059669]">
                              CASH RÉINVESTISSABLE
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="h-4 w-4 text-slate-400 ml-1 inline" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">Ce cash reste en société et peut être réinvesti (immo, placements) sans fiscalité tant qu'il ne sort pas</p>
                                </TooltipContent>
                              </Tooltip>
                            </td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-3 px-4 font-bold text-lg text-[#059669] ${i === 0 ? 'bg-[#52fbb0]/10' : ''}`}>
                                {s.cashSociete > 0 ? `${s.cashSociete.toLocaleString("fr-FR")} €` : '-'}
                              </td>
                            ))}
                          </tr>

                          <tr className="bg-slate-900 text-white">
                            <td className="py-4 px-4 font-bold text-lg">PATRIMOINE TOTAL</td>
                            {results.statuts.map((s, i) => (
                              <td key={s.name} className={`text-right py-4 px-4 font-bold text-xl ${i === 0 ? 'text-[#52fbb0]' : ''}`}>
                                {s.patrimoineTotal.toLocaleString("fr-FR")} €
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>

                  {/* Cards avantages par statut */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {results.statuts.map((statut, index) => (
                      <Card key={statut.name} className={index === 0 ? "ring-2 ring-[#52fbb0]" : ""}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center justify-between">
                            {statut.shortName}
                            {index === 0 && (
                              <span className="text-xs bg-[#52fbb0] text-slate-900 px-2 py-1 rounded-full">
                                Recommandé
                              </span>
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Avantages</p>
                              <ul className="text-xs space-y-1">
                                {statut.avantages.slice(0, 3).map((a, i) => (
                                  <li key={i} className="flex items-start gap-1 text-slate-600">
                                    <CheckCircle className="h-3 w-3 text-[#52fbb0] mt-0.5 flex-shrink-0" />
                                    {a}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {statut.warning && (
                              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                                {statut.warning}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <Button
                      onClick={() => setShowModal(true)}
                      className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-semibold rounded-full px-8 py-6"
                      data-testid="btn-download-pdf"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Télécharger le comparatif PDF
                    </Button>
                    <Button
                      onClick={saveSimulation}
                      variant="outline"
                      className="rounded-full px-8 py-6"
                      data-testid="btn-save"
                    >
                      <Save className="mr-2 h-5 w-5" />
                      Sauvegarder
                    </Button>
                    <Button
                      onClick={() => setActiveTab("inputs")}
                      variant="ghost"
                      className="rounded-full px-8 py-6"
                    >
                      Modifier les paramètres
                    </Button>
                  </div>

                  {/* CTA */}
                  <Card className="bg-slate-900 text-white border-0">
                    <CardContent className="p-8 text-center">
                      <h3 className="font-manrope font-bold text-2xl mb-3">
                        Besoin d'optimiser davantage ?
                      </h3>
                      <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                        FREELANCEURS.io vous accompagne dans le choix et l'optimisation de votre statut.
                        Holding, stratégie dividendes, investissements : on s'occupe de tout.
                      </p>
                      <a href="https://freelanceurs.app" target="_blank" rel="noopener noreferrer">
                        <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] rounded-full px-8">
                          Prendre rendez-vous
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Lead Capture Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md" data-testid="lead-modal">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl">
                Téléchargez votre comparatif
              </DialogTitle>
              <DialogDescription>
                Recevez votre analyse détaillée en PDF.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLeadSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">Prénom</Label>
                  <Input
                    id="firstName"
                    value={leadForm.firstName}
                    onChange={(e) => setLeadForm({ ...leadForm, firstName: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="input-firstname"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Nom</Label>
                  <Input
                    id="lastName"
                    value={leadForm.lastName}
                    onChange={(e) => setLeadForm({ ...leadForm, lastName: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="input-lastname"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="input-phone"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-semibold rounded-full py-6"
                disabled={isSubmitting}
                data-testid="btn-submit-lead"
              >
                {isSubmitting ? "Envoi en cours..." : "Télécharger le PDF"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
